/**
 * disciplineEnforcer — WRAPPER-DISCIPLINE-EXT-1
 *
 * Pure function output gate. Receives a structured EnforcerEnvelope
 * from a governed wrapper and returns an EnforcerVerdict before
 * the result reaches the caller.
 *
 * Zero side effects. No Firebase imports. No model SDK imports.
 * Property-fuzzable. KAUDIT-replayable.
 *
 * Gate ordering is LOCKED at v1 (doctrine-fixed): 1→2→3→4→5→6→7→8→9.
 * Short-circuit on first trip. Gate 8 (roughening) trips to a
 * roughened stated_position, not abstention.
 *
 * Three return types are EXHAUSTIVE and EXCLUSIVE:
 *   stated_position | abstention | silence
 *
 * Adding, removing, or renaming a gate requires a registry decision
 * plus founder approval. Silent expansion is FORBIDDEN.
 */

import { checkAnchorInversion } from './gates/gate1-anchor-inversion.js';
import { checkCoherenceRestoration } from './gates/gate2-coherence-restoration.js';
import { checkConfidenceFloor } from './gates/gate3-confidence-floor.js';
import { checkFreshness } from './gates/gate4-freshness.js';
import { checkCostWeightedThreshold } from './gates/gate5-cost-weighted-threshold.js';
import { checkPatternReDerivation } from './gates/gate6-pattern-re-derivation.js';
import { checkSilentPriorUpdate } from './gates/gate7-silent-prior-update.js';
import { checkRoughening } from './gates/gate8-roughening.js';
import { checkEffortProportionality } from './gates/gate9-effort-proportionality.js';

export const ENFORCER_VERSION = 'discipline-enforcer-v1.0';
export const GATE_REGISTRY_VERSION = 'gate-registry-v1.0';

// COG-PRIMITIVES-EXT-1: Primitive library version reference.
// Primitives are pure reasoning tools invoked during deliberation — they bypass
// the output gates. Only final synthesis outputs pass through enforceOutput().
export const PRIMITIVE_LIBRARY_VERSION = 'cog-primitives-v1.0';

// KCC-STANDARD-COUNCIL-1 Decision 11 — Per-substrate KCRIT cadence config.
// Values: fraction of decisions in each band that receive KCRIT review (1.0 = 100%, 0.25 = 25%).
// Sovereign tier overrides to 1.0 for all bands regardless of this config.
// Failure mode when KCRIT offline: EXISTENTIAL/MATERIAL → fail-CLOSED; ELEVATED/ROUTINE → fail-OPEN.
export const KCRIT_CADENCE = {
  EXISTENTIAL: 1.0,
  MATERIAL:    1.0,
  ELEVATED:    0.25,
  ROUTINE:     0.0,
  SOVEREIGN_OVERRIDE: 1.0,
};

// KCC-STANDARD-COUNCIL-1 Decision 10 — Synthesis HOLD triggers.
// A synthesis with materialityBand === 'EXISTENTIAL' AND any of these triggers fires
// must be routed to synthesis_holds (HELD status) before reaching any downstream consumer.
// Pure-dollar EXISTENTIAL (no triggers) ships for review-after-execute.
export const HOLD_TRIGGERS = [
  'equity-affecting',
  'irreversible',
  'litigation-touching',
  'acquirer-narrative-shaping',
];

// Return types: silence for confidence failures (no output beats wrong);
// abstention for all other trips; stated_position on clean pass.
const GATE_RETURN_TYPES = {
  anchor_inversion: 'abstention',
  coherence_restoration: 'abstention',
  confidence_floor: 'silence',
  freshness: 'abstention',
  cost_weighted_threshold: 'abstention',
  pattern_re_derivation: 'abstention',
  silent_prior_update: 'abstention',
  roughening: 'stated_position', // special — modifies, does not block
  effort_proportionality: 'abstention',
};

/**
 * @typedef {object} EnforcerEnvelope
 * @property {string} tenantId
 * @property {string} module
 * @property {string} decisionType
 * @property {string} modelId
 * @property {string} rawOutput
 * @property {any} parsedOutput
 * @property {number} confidence  - 0..1
 * @property {{ isDeclarative: boolean, materialityBand: string, isHighStakes: boolean,
 *              decisionClass: string, freshnessWindowMs?: number,
 *              sourceTimestamps?: number[], bayesianPriorRef?: string,
 *              effortBudget?: { tokensSpent: number, targetTokens: number } }} context
 */

/**
 * @typedef {object} GateResult
 * @property {string} gateName
 * @property {'pass'|'trip'} verdict
 * @property {string|null} reason
 * @property {any} payload
 */

/**
 * @typedef {object} EnforcerVerdict
 * @property {'stated_position'|'abstention'|'silence'} returnType
 * @property {string} reason
 * @property {string|null} trippedGate
 * @property {GateResult[]} gateResults
 * @property {{ type: 'roughened', originalConfidence: number, adjustedConfidence: number }|undefined} outputModification
 * @property {string} enforcerVersion
 * @property {string} gateRegistryVersion
 */

const GATES = [
  { name: 'anchor_inversion', fn: checkAnchorInversion },
  { name: 'coherence_restoration', fn: checkCoherenceRestoration },
  { name: 'confidence_floor', fn: checkConfidenceFloor },
  { name: 'freshness', fn: checkFreshness },
  { name: 'cost_weighted_threshold', fn: checkCostWeightedThreshold },
  { name: 'pattern_re_derivation', fn: checkPatternReDerivation },
  { name: 'silent_prior_update', fn: checkSilentPriorUpdate },
  { name: 'roughening', fn: checkRoughening },
  { name: 'effort_proportionality', fn: checkEffortProportionality },
];

/**
 * Apply all 9 output gates to a wrapper result envelope.
 *
 * @param {EnforcerEnvelope} envelope
 * @param {object} [gateConfig] - per-gate config overrides (e.g. tenant floor overrides)
 * @returns {EnforcerVerdict}
 */
export function enforceOutput(envelope, gateConfig = {}) {
  const gateResults = [];

  for (const gate of GATES) {
    const config = gateConfig[gate.name] || {};
    const result = gate.fn(envelope, config);
    gateResults.push(result);

    if (result.verdict === 'trip') {
      const returnType = GATE_RETURN_TYPES[gate.name] || 'abstention';

      if (gate.name === 'roughening') {
        // Roughening is a non-blocking trip: modifies and returns stated_position.
        return {
          returnType: 'stated_position',
          reason: result.reason || 'precision_reduced',
          trippedGate: gate.name,
          gateResults,
          outputModification: {
            type: 'roughened',
            originalConfidence: result.payload.originalConfidence,
            adjustedConfidence: result.payload.roughenedConfidence,
          },
          enforcerVersion: ENFORCER_VERSION,
          gateRegistryVersion: GATE_REGISTRY_VERSION,
        };
      }

      return {
        returnType,
        reason: result.reason || `${gate.name}_tripped`,
        trippedGate: gate.name,
        gateResults,
        outputModification: undefined,
        enforcerVersion: ENFORCER_VERSION,
        gateRegistryVersion: GATE_REGISTRY_VERSION,
      };
    }
  }

  return {
    returnType: 'stated_position',
    reason: 'all_gates_passed',
    trippedGate: null,
    gateResults,
    outputModification: undefined,
    enforcerVersion: ENFORCER_VERSION,
    gateRegistryVersion: GATE_REGISTRY_VERSION,
  };
}

/**
 * Decision 11: Determines whether KCRIT review should fire for this invocation.
 * Uses deterministic routing for EXISTENTIAL/MATERIAL (always), pseudo-random 25% sample
 * for ELEVATED, and 0% for ROUTINE. Sovereign tier always returns true.
 *
 * @param {string} materialityBand - EXISTENTIAL | MATERIAL | ELEVATED | ROUTINE
 * @param {boolean} isSovereign    - true if tenant tier === 'sovereign'
 * @param {string} [decisionId]    - used to seed the pseudo-random ELEVATED sample
 * @returns {boolean}
 */
export function shouldApplyKcritReview(materialityBand, isSovereign = false, decisionId = '') {
  if (isSovereign) return true;
  const cadence = KCRIT_CADENCE[materialityBand] ?? 0;
  if (cadence >= 1.0) return true;
  if (cadence <= 0.0) return false;
  // Pseudo-random deterministic sample: hash decisionId to a 0..1 float
  const hash = _simpleHash(decisionId);
  return (hash % 100) / 100 < cadence;
}

/**
 * Decision 10: Determines whether a synthesis should be held pending founder review.
 * Returns true only when band === EXISTENTIAL AND at least one trigger is present.
 *
 * @param {string} materialityBand   - EXISTENTIAL | MATERIAL | ELEVATED | ROUTINE
 * @param {string[]} triggers        - trigger flags set on the decision (from context.holdTriggers)
 * @returns {boolean}
 */
export function shouldHoldSynthesis(materialityBand, triggers = []) {
  if (materialityBand !== 'EXISTENTIAL') return false;
  return triggers.some(t => HOLD_TRIGGERS.includes(t));
}

function _simpleHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export default enforceOutput;

// ─── AEGIS-GOV-0 additions ───────────────────────────────────────────────────
// The symbols below are side-effecting and distinct from the pure enforceOutput
// core above. They implement the operator-dignity contract required by the
// GOVERNED-WRAPPERS-SHIP-1 session.

import DataService from '../DataService.js';
import { INFANTILIZING_PATTERN_LIST } from '../aegis/aegis7PresentationContract.js';

/**
 * Eight named cognitive frameworks that govern deliberation discipline.
 * Subset of the foundational tier from primitiveLibrary — the eight most
 * directly load-bearing for governed AI calls.
 */
export const COGNITIVE_FRAMEWORKS = Object.freeze({
  INVERSION:       { id: 'invert',       tier: 'foundational', description: 'Invert the problem — what would make this fail?' },
  STEELMANNING:    { id: 'steelman',     tier: 'foundational', description: 'Construct the strongest form of the opposing argument.' },
  BAYESIAN_UPDATE: { id: 'bayesian',     tier: 'foundational', description: 'Update probability estimates on new evidence.' },
  SECOND_ORDER:    { id: 'secondOrder',  tier: 'foundational', description: 'Trace second- and third-order consequences.' },
  OUTSIDE_VIEW:    { id: 'outsideView',  tier: 'foundational', description: 'Reference class forecasting — what do base rates say?' },
  DECOMPOSITION:   { id: 'decompose',    tier: 'foundational', description: 'Break the problem into independently-analyzable components.' },
  PRE_MORTEM:      { id: 'premortem',    tier: 'foundational', description: 'Assume failure — work backward to causes.' },
  CALIBRATION:     { id: 'calibrate',    tier: 'foundational', description: 'Adjust confidence to match actual track record.' },
});

/**
 * Custom error thrown when a dignity violation is detected in input.
 */
export class DignityViolationError extends Error {
  constructor(matchedPatterns) {
    super(`Operator dignity violation detected: ${matchedPatterns.join(', ')}`);
    this.name = 'DignityViolationError';
    this.code = 'DIGNITY_VIOLATION';
    this.matchedPatterns = matchedPatterns;
  }
}

/**
 * Check input text against operator dignity violation patterns.
 * Throws DignityViolationError on match — never returns false silently.
 *
 * Uses the canonical INFANTILIZING_PATTERN_LIST from AEGIS-7 presentation
 * contract as the violation pattern source (§4 substitute per decisions.md).
 *
 * @param {string} input
 * @throws {DignityViolationError}
 */
export function checkOperatorDignity(input) {
  if (typeof input !== 'string' || input.length === 0) return;
  const lowered = input.toLowerCase();
  const hits = INFANTILIZING_PATTERN_LIST.filter(p => lowered.includes(p));
  if (hits.length > 0) throw new DignityViolationError(hits);
}

/**
 * Wrapper: runs checkOperatorDignity on context.input before invoking fn.
 * If a dignity violation is detected, the violation is gated (enforceDignityGate)
 * and the error re-thrown — fn is never called.
 *
 * @param {Function} fn
 * @param {{ input: string, tenantId: string, agentId?: string, module?: string }} context
 * @returns {Promise<any>}
 */
export async function withDisciplineEnforcer(fn, context) {
  const input = context?.input ?? '';
  const tenantId = context?.tenantId;
  if (!tenantId) {
    const err = new Error('withDisciplineEnforcer: tenantId is required');
    err.code = 'DISCIPLINE_MISSING_TENANT';
    throw err;
  }
  try {
    checkOperatorDignity(input);
  } catch (violationErr) {
    if (violationErr instanceof DignityViolationError) {
      await enforceDignityGate({
        tenantId,
        agentId: context?.agentId || 'unknown',
        module: context?.module || 'unknown',
        input,
        matchedPatterns: violationErr.matchedPatterns,
      });
    }
    throw violationErr;
  }
  return fn();
}

/**
 * Write a dignity violation record to `cognition_dignity_violations` via DataService.
 * tenantId is required — hard throw if absent (never fallback).
 *
 * @param {{ tenantId: string, agentId: string, module: string, input: string, matchedPatterns: string[] }} record
 */
export async function enforceDignityGate(record) {
  if (!record?.tenantId) {
    const err = new Error('enforceDignityGate: tenantId is required');
    err.code = 'DIGNITY_GATE_MISSING_TENANT';
    throw err;
  }
  const ds = new DataService(record.tenantId);
  await ds.create('cognition_dignity_violations', {
    tenantId:        record.tenantId,
    agentId:         record.agentId || 'unknown',
    module:          record.module  || 'unknown',
    pattern:         (record.matchedPatterns || []).join(','),
    input:           typeof record.input === 'string' ? record.input.slice(0, 2000) : '',
    createdAt:       new Date().toISOString(),
    deletedAt:       null,
  });
}

// ─── AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1 additions ──────────────────────────
// Model routing, token budget, system-prompt, and caller-registration enforcement.
// These are pure throws — callers must satisfy all four contracts before any
// AI invocation. Called at wrapper entry; never fire post-call.

/**
 * Canonical model IDs per tier.
 * TOKEN-DIET-BINDING: Haiku = extraction/classification; Sonnet = generation/analysis;
 * Opus = council deliberation only (never surface-level callers).
 */
export const MODEL_ROUTING = Object.freeze({
  extraction:  'claude-haiku-4-5-20251001',
  generation:  'claude-sonnet-4-6',
  council:     'claude-opus-4-6',
});

/**
 * Maximum output tokens per model tier.
 * Hard caps — no caller may exceed these regardless of context size.
 */
export const TOKEN_BUDGET = Object.freeze({
  'claude-haiku-4-5-20251001': 512,
  'claude-sonnet-4-6':         4096,
  'claude-opus-4-6':           8192,
});

/**
 * Registered caller modules — only these may invoke governed wrappers.
 * New modules must register here and in the wrapper-discipline registry.
 */
export const REGISTERED_MODULES = Object.freeze(new Set([
  'argus', 'artemis', 'aegis', 'exec', 'cog-council', 'kcc',
  'sentinel', 'cx', 'comms', 'tms', 'ims', 'wms', 'doc',
  'iot', 'fleet', 'intl', 'fin', 'integration', 'kbrain',
  'krol', 'kcon', 'kion', 'kpol', 'kfol', 'kiol',
  'command-center',
  'demo', 'test',
]));

/**
 * Throw if Opus is requested by a non-council caller.
 * Council calls must originate from module 'cog-council' or 'kcc'.
 *
 * @param {string} requestedModel - model ID from params
 * @param {string} callerModule   - params.module value
 * @throws {{ code: 'MODEL_TIER_VIOLATION' }}
 */
export function enforceModelTier(requestedModel, callerModule) {
  if (requestedModel === MODEL_ROUTING.council) {
    if (callerModule !== 'cog-council' && callerModule !== 'kcc') {
      const err = new Error(
        `enforceModelTier: Opus (${MODEL_ROUTING.council}) is restricted to cog-council/kcc. ` +
        `Caller module '${callerModule}' must use Sonnet or Haiku.`
      );
      err.code = 'MODEL_TIER_VIOLATION';
      throw err;
    }
  }
}

/**
 * Throw if requestedMaxTokens exceeds the hard cap for the given model tier.
 * Pass 0 / undefined to skip the check (wrapper enforces the cap itself).
 *
 * @param {string} modelId
 * @param {number} [requestedMaxTokens]
 * @throws {{ code: 'TOKEN_BUDGET_EXCEEDED' }}
 */
export function enforceTokenBudget(modelId, requestedMaxTokens) {
  if (requestedMaxTokens == null || requestedMaxTokens === 0) return;
  const cap = TOKEN_BUDGET[modelId];
  if (cap == null) return; // unknown model — let the server reject
  if (requestedMaxTokens > cap) {
    const err = new Error(
      `enforceTokenBudget: requested max_tokens ${requestedMaxTokens} exceeds ` +
      `cap ${cap} for model '${modelId}'.`
    );
    err.code = 'TOKEN_BUDGET_EXCEEDED';
    throw err;
  }
}

/**
 * Throw if systemPrompt is absent or empty.
 * Every governed AI call must carry a non-empty system prompt — no open-ended calls.
 *
 * @param {string|undefined} systemPrompt
 * @throws {{ code: 'SYSTEM_PROMPT_REQUIRED' }}
 */
export function enforceSystemPrompt(systemPrompt) {
  if (typeof systemPrompt !== 'string' || systemPrompt.trim().length === 0) {
    const err = new Error('enforceSystemPrompt: a non-empty system prompt is required on every governed call.');
    err.code = 'SYSTEM_PROMPT_REQUIRED';
    throw err;
  }
}

/**
 * Warn (non-throwing) if caller module is not in the registered set.
 * Logging only at this tier — CI check-wrapper-discipline.mjs is the hard gate.
 *
 * @param {string} callerModule
 */
export function enforceCallerRegistration(callerModule) {
  if (!REGISTERED_MODULES.has(callerModule)) {
    console.warn(
      `[disciplineEnforcer] Unregistered caller module '${callerModule}'. ` +
      `Add to REGISTERED_MODULES and the wrapper-discipline registry.`
    );
  }
}
